/**
 * Instance i18next de l’application (DECISIONS §9).
 *
 * Deux namespaces, une seule instance :
 * - `ui` : catalogue d’interface, propriété de eq-dev-frontend (`src/i18n/locales/*.json`) ;
 * - `content` : catalogue de contenu, propriété de eq-game-design (`packages/content/i18n/*.json`).
 *
 * Le catalogue de contenu n’est **pas** recopié ici : il est importé depuis `@elemora/content`,
 * source unique de vérité (DECISIONS §9.1). Recopier ces clés créerait deux vérités à garder
 * synchronisées, exactement ce que la parité stricte cherche à éviter.
 *
 * Résolution de la langue : `user.locale` → cookie `elemora_locale` → `navigator.language` → `fr`.
 * En développement, une clé absente s’affiche `⟦namespace:clé⟧` **et** journalise un avertissement :
 * jamais de silence, jamais de texte anglais par défaut.
 */
import contenuEn from '@elemora/content/i18n/en.json';
import contenuFr from '@elemora/content/i18n/fr.json';
import { COOKIE_LOCALE, I18N_NAMESPACES, LOCALES, type Locale } from '@elemora/shared/constants';
import i18next, { type i18n as InstanceI18n } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { lireCookie, memoriserLocale } from './locale-cookie';
import { env } from '../app/env';
import en from './locales/en.json';
import fr from './locales/fr.json';

/** Namespaces exposés par l’instance (DECISIONS §9.1). */
export const NAMESPACE_INTERFACE = 'ui' as const;
export const NAMESPACE_CONTENU = 'content' as const;

/** Préfixe des `nameKey` de contenu, ex. `content.tour.tour_feu_mono.name`. */
export const PREFIXE_CLE_CONTENU = 'content.' as const;

const RESSOURCES = {
  fr: { ui: fr.ui, content: contenuFr },
  en: { ui: en.ui, content: contenuEn },
} as const;

/**
 * Ramène une valeur quelconque à une locale livrée.
 *
 * `fr-FR`, `FR` et `en_GB` sont acceptés : seule la sous-étiquette primaire compte, ce qui évite
 * de dépendre des variantes régionales que le navigateur renvoie.
 */
export function normaliserLocale(valeur: string | null | undefined): Locale | null {
  if (valeur === null || valeur === undefined) return null;
  const primaire = valeur.trim().toLowerCase().split(/[-_]/)[0];
  if (primaire === undefined) return null;
  return (LOCALES as readonly string[]).includes(primaire) ? (primaire as Locale) : null;
}

/** Ordre de résolution de la langue (DECISIONS §9.3). */
export function detecterLocale(localeUtilisateur?: string | null): Locale {
  const depuisUtilisateur = normaliserLocale(localeUtilisateur);
  if (depuisUtilisateur) return depuisUtilisateur;

  const depuisCookie = normaliserLocale(lireCookie(COOKIE_LOCALE));
  if (depuisCookie) return depuisCookie;

  const navigateur = typeof navigator === 'undefined' ? null : navigator.language;
  const depuisNavigateur = normaliserLocale(navigateur);
  if (depuisNavigateur) return depuisNavigateur;

  return env.localeDefaut;
}

/**
 * Valeur renvoyée pour une clé introuvable.
 *
 * En production le catalogue de repli (`fr`) prend le relais ; s’il manque aussi, la clé brute
 * s’affiche — un défaut visible, jamais un libellé inventé.
 */
function cleManquante(cle: string): string {
  if (import.meta.env.DEV) {
    // Volontairement visible : une clé manquante doit être corrigée avant la mise en production.
    console.warn(`[i18n] clé de traduction manquante : ${cle}`);
    return `⟦${cle}⟧`;
  }
  return cle;
}

/**
 * Fabrique une instance i18next isolée (utile aux tests, qui ne doivent pas partager d’état).
 *
 * `initImmediate: false` : les ressources sont déjà en mémoire, l’initialisation est synchrone.
 * Sans cela, le premier rendu React s’effectuerait avant le chargement des catalogues.
 */
export function creerInstanceI18n(localeInitiale: Locale = detecterLocale()): InstanceI18n {
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    resources: RESSOURCES,
    lng: localeInitiale,
    fallbackLng: env.localeDefaut,
    supportedLngs: [...LOCALES],
    ns: [...I18N_NAMESPACES],
    defaultNS: NAMESPACE_INTERFACE,
    fallbackNS: NAMESPACE_INTERFACE,
    initImmediate: false,
    interpolation: { escapeValue: false },
    returnNull: false,
    appendNamespaceToMissingKey: true,
    parseMissingKeyHandler: cleManquante,
    react: { useSuspense: false },
  });
  return instance;
}

/** Instance utilisée par l’application (et par react-i18next via `initReactI18next`). */
export const instanceI18n = creerInstanceI18n();

/** Locale courante, toujours une locale livrée. */
export function localeCourante(): Locale {
  return normaliserLocale(instanceI18n.language) ?? env.localeDefaut;
}

/**
 * Change la langue **à chaud**, sans rechargement (DECISIONS §9.3), et mémorise le choix.
 *
 * Le serveur est informé en M2 (`PATCH /me`) : en M1 le catalogue de langue n’a pas de colonne
 * à mettre à jour côté API autrement que par `PATCH /me`, non livré à ce jalon.
 */
export async function changerLangue(locale: Locale): Promise<void> {
  if (localeCourante() === locale) {
    memoriserLocale(locale);
    return;
  }
  await instanceI18n.changeLanguage(locale);
  memoriserLocale(locale);
}

/**
 * Applique la langue du compte connecté (priorité haute, DECISIONS §9.3).
 *
 * Appelée après connexion ou restauration de session : le choix du compte prime sur le cookie
 * et sur la langue du navigateur.
 */
export function synchroniserLangueUtilisateur(localeUtilisateur: string | null | undefined): void {
  const locale = normaliserLocale(localeUtilisateur);
  if (!locale) return;
  memoriserLocale(locale);
  if (localeCourante() !== locale) {
    void instanceI18n.changeLanguage(locale);
  }
}

/**
 * Transforme un `nameKey` de contenu (`content.tour.x.name`) en clé i18next explicite
 * (`content:tour.x.name`), conformément à la notation de DECISIONS §9.2.
 */
export function cleTraductionContenu(nameKey: string): string {
  const sansPrefixe = nameKey.startsWith(PREFIXE_CLE_CONTENU)
    ? nameKey.slice(PREFIXE_CLE_CONTENU.length)
    : nameKey;
  return `${NAMESPACE_CONTENU}:${sansPrefixe}`;
}

export default instanceI18n;

export { formaterNombre, formaterDate } from './format';
